/**
 * Autocomplete Service for DSL Functions
 * Provides intelligent function suggestions and parameter hints
 */

import { 
  searchFunctions, 
  getFunctionMetadata, 
  getFunctionTemplate,
  getFunctionSignature,
  DSL_CONSTANTS,
  FUNCTION_CATEGORIES
} from './functionMetadata.js';

/**
 * Parse current input to determine context for autocomplete
 */
export const parseInputContext = (input, cursorPosition) => {
  const beforeCursor = input.substring(0, cursorPosition);
  const afterCursor = input.substring(cursorPosition);
  
  // Find the current word being typed
  const wordMatch = beforeCursor.match(/(\w+)$/);
  const currentWord = wordMatch ? wordMatch[1] : '';
  
  // Check if we're inside function parentheses
  const openParens = (beforeCursor.match(/\(/g) || []).length;
  const closeParens = (beforeCursor.match(/\)/g) || []).length;
  const insideFunction = openParens > closeParens;
  
  // Find the current function context
  let currentFunction = null;
  let parameterIndex = 0;
  
  if (insideFunction) {
    // Work backwards to find the function name
    const functionMatch = beforeCursor.match(/(\w+)\s*\([^)]*$/);
    if (functionMatch) {
      currentFunction = functionMatch[1];
      // Count commas to determine parameter index
      const afterFunctionStart = beforeCursor.substring(functionMatch.index + functionMatch[1].length + 1);
      parameterIndex = (afterFunctionStart.match(/,/g) || []).length;
    }
  }
  
  return {
    currentWord,
    insideFunction,
    currentFunction,
    parameterIndex,
    beforeCursor,
    afterCursor
  };
};

/**
 * Get autocomplete suggestions based on current input context
 */
export const getAutocompleteSuggestions = (input, cursorPosition, options = {}) => {
  const {
    maxSuggestions = 10,
    includeConstants = true,
    categoryFilter = null
  } = options;
  
  const context = parseInputContext(input, cursorPosition);
  const suggestions = [];
  
  // If we're inside a function, provide parameter hints
  if (context.insideFunction && context.currentFunction) {
    const signature = getFunctionSignature(context.currentFunction);
    if (signature) {
      suggestions.push({
        type: 'parameter_hint',
        function: context.currentFunction,
        signature: signature.signature,
        currentParam: context.parameterIndex,
        description: signature.description,
        example: signature.example,
        name: `${context.currentFunction} parameters`,
        score: 100
      });
    }
  }
  
  // Get function suggestions based on current word
  if (context.currentWord && context.currentWord.length > 0) {
    try {
      const functionSuggestions = searchFunctions(context.currentWord, maxSuggestions)
        .filter(func => !categoryFilter || func.category === categoryFilter)
        .map(func => ({
          type: 'function',
          name: func.name,
          template: getFunctionTemplate(func.name),
          description: func.description,
          category: func.category,
          example: func.example,
          insertText: func.name,
          score: func.score || 90
        }));
      
      suggestions.push(...functionSuggestions);
      
      // Add constant suggestions if enabled
      if (includeConstants) {
        const constantSuggestions = Object.entries(DSL_CONSTANTS)
          .filter(([name]) => name.toLowerCase().includes(context.currentWord.toLowerCase()))
          .map(([name, constant]) => ({
            type: 'constant',
            name,
            value: constant.value,
            description: constant.description,
            insertText: name,
            score: name.toLowerCase().startsWith(context.currentWord.toLowerCase()) ? 85 : 75
          }));
        
        suggestions.push(...constantSuggestions);
      }
    } catch (error) {
      console.error('Error getting function suggestions:', error);
    }
  }
  
  // Sort by score and limit results
  return suggestions
    .filter(s => s && s.name) // Ensure valid suggestions
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, maxSuggestions);
};

/**
 * Get parameter hints for a specific function
 */
export const getParameterHints = (functionName, parameterIndex = 0) => {
  const metadata = getFunctionMetadata(functionName);
  if (!metadata) return null;
  
  return {
    functionName,
    signature: metadata.signature,
    parameters: metadata.params,
    currentParameter: parameterIndex < metadata.params.length ? metadata.params[parameterIndex] : null,
    description: metadata.description,
    example: metadata.example,
    isVariadic: metadata.variadic
  };
};

/**
 * Insert suggestion into input at cursor position
 */
export const insertSuggestion = (input, cursorPosition, suggestion) => {
  const context = parseInputContext(input, cursorPosition);
  
  let insertText = suggestion.insertText || suggestion.name;
  let newCursorPosition = cursorPosition;
  
  // If it's a function suggestion, add parentheses and position cursor inside
  if (suggestion.type === 'function') {
    const metadata = getFunctionMetadata(suggestion.name);
    if (metadata && metadata.params.length > 0) {
      insertText = `${suggestion.name}(`;
      newCursorPosition = cursorPosition - context.currentWord.length + insertText.length;
    } else {
      insertText = `${suggestion.name}()`;
      newCursorPosition = cursorPosition - context.currentWord.length + insertText.length - 1;
    }
  }
  
  // Replace the current word with the suggestion
  const beforeWord = input.substring(0, cursorPosition - context.currentWord.length);
  const afterCursor = input.substring(cursorPosition);
  const newInput = beforeWord + insertText + afterCursor;
  
  return {
    newInput,
    newCursorPosition
  };
};

/**
 * Get category-based suggestions
 */
export const getSuggestionsByCategory = (category, limit = 20) => {
  const categoryInfo = FUNCTION_CATEGORIES[category];
  if (!categoryInfo) return [];
  
  return Object.entries(DSL_FUNCTION_METADATA)
    .filter(([_, metadata]) => metadata.category === category)
    .slice(0, limit)
    .map(([name, metadata]) => ({
      type: 'function',
      name,
      template: getFunctionTemplate(name),
      description: metadata.description,
      category: metadata.category,
      example: metadata.example,
      insertText: name
    }));
};

/**
 * Validate function usage and provide suggestions
 */
export const validateFunctionUsage = (input) => {
  const issues = [];
  
  // Check for unmatched parentheses
  const openParens = (input.match(/\(/g) || []).length;
  const closeParens = (input.match(/\)/g) || []).length;
  
  if (openParens > closeParens) {
    issues.push({
      type: 'syntax',
      message: `Missing ${openParens - closeParens} closing parenthesis`,
      severity: 'error'
    });
  } else if (closeParens > openParens) {
    issues.push({
      type: 'syntax',
      message: `Extra ${closeParens - openParens} closing parenthesis`,
      severity: 'error'
    });
  }
  
  // Check for unknown functions
  const functionMatches = input.match(/(\w+)\s*\(/g);
  if (functionMatches) {
    functionMatches.forEach(match => {
      const funcName = match.replace(/\s*\($/, '');
      if (!getFunctionMetadata(funcName)) {
        issues.push({
          type: 'unknown_function',
          message: `Unknown function: ${funcName}`,
          severity: 'warning',
          suggestions: searchFunctions(funcName, 3).map(f => f.name)
        });
      }
    });
  }
  
  return issues;
};

export default {
  parseInputContext,
  getAutocompleteSuggestions,
  getParameterHints,
  insertSuggestion,
  getSuggestionsByCategory,
  validateFunctionUsage
};